import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { useDealerAdmin } from '../../contexts/DealerAdminContext';
import { ROLES } from '../../utils/constants';
import MainLayout from '../../components/layout/MainLayout';
import {
  getDealerContexts,
  getDealerTenantOverview,
  getDealerTenantSupport,
  getDealerTenants,
} from '../../api/backendApi';
import { ROUTES } from '../../utils/constants';
import { useAuth } from '../../contexts/AuthContext';
import { shouldRedirectToOnboard } from '../../utils/agreementGate';
import { assignedTenantDisplayName, rowsFrom, safeDealerError, selectAssignedTenant, selectDealer } from '../Admin/dealerAccess';
import { rememberPathForDealerLogin } from '../../utils/dealerHostRouting';
import CustomerSubscriptionSection from './CustomerSubscriptionSection';
import DealerUsageReport from './DealerUsageReport';

function NotAuthorized({ onLeave }) {
  return (
    <MainLayout scrollMain>
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have access to that Dealer or tenant.</Alert>
        {onLeave ? (
          <Button sx={{ mt: 2 }} onClick={onLeave}>Leave tenant</Button>
        ) : null}
      </Box>
    </MainLayout>
  );
}

export default function DealerWorkspacePage() {
  const { dealerId: requestedDealerId, tenantId: requestedTenantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, authReady, agreementStatus, agreementStatusReady, userClaims, userData } = useAuth();
  const dealerAdmin = useDealerAdmin();
  const userRole = userClaims?.role || userData?.role;
  const [contexts, setContexts] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [tenantsLoaded, setTenantsLoaded] = useState(false);
  const [overview, setOverview] = useState(null);
  const [support, setSupport] = useState(null);
  const [error, setError] = useState('');
  const [unauthorized, setUnauthorized] = useState(false);

  const selection = contexts
    ? selectDealer(contexts, requestedDealerId)
    : { dealer: null, unauthorized: false, needsSelection: false };
  const tenantSelection = selectAssignedTenant(tenants, requestedTenantId, { loaded: tenantsLoaded });

  useEffect(() => {
    getDealerContexts()
      .then((response) => setContexts(rowsFrom(response)))
      .catch((err) => {
        setContexts([]);
        setError(safeDealerError(err));
      });
  }, []);

  useEffect(() => {
    if (!contexts) return;
    if (selection.unauthorized) {
      setUnauthorized(true);
      return;
    }
    if (selection.dealer && !requestedDealerId) {
      navigate(ROUTES.DEALER_WORKSPACE_DEALER.replace(':dealerId', selection.dealer.dealerId), { replace: true });
    }
  }, [contexts, requestedDealerId, selection.dealer, selection.unauthorized, navigate]);

  useEffect(() => {
    if (!selection.dealer) return;
    setUnauthorized(false);
    setTenantsLoaded(false);
    getDealerTenants(selection.dealer.dealerId)
      .then((response) => {
        setTenants(rowsFrom(response));
        setTenantsLoaded(true);
      })
      .catch((err) => {
        setTenants([]);
        setTenantsLoaded(true);
        if (String(err?.message || '').includes('(403)')) setUnauthorized(true);
        else setError(safeDealerError(err));
      });
  }, [selection.dealer]);

  useEffect(() => {
    if (!selection.dealer?.dealerId || !requestedTenantId) return;
    if (dealerAdmin.dealerId === selection.dealer.dealerId && dealerAdmin.tenantId === requestedTenantId) return;
    dealerAdmin.enter(selection.dealer.dealerId, requestedTenantId, {
      tenantName: assignedTenantDisplayName(tenantSelection.tenant),
    });
  }, [dealerAdmin.dealerId, dealerAdmin.enter, dealerAdmin.tenantId, requestedTenantId, selection.dealer, tenantSelection.tenant]);

  useEffect(() => {
    if (!selection.dealer || !requestedTenantId) {
      setOverview(null);
      setSupport(null);
      return;
    }
    if (!tenantsLoaded) return;
    if (tenantSelection.unauthorized) {
      setUnauthorized(true);
      return;
    }
    const dealerId = selection.dealer.dealerId;
    let live = true;
    dealerAdmin.enter(dealerId, requestedTenantId, {
      tenantName: assignedTenantDisplayName(tenantSelection.tenant),
      algoDeviceSupport: false,
    });
    Promise.all([
      getDealerTenantOverview(dealerId, requestedTenantId),
      getDealerTenantSupport(dealerId, requestedTenantId),
    ]).then(([overviewResponse, supportResponse]) => {
      if (!live) return;
      setOverview(overviewResponse.data);
      setSupport(supportResponse.data);
      setUnauthorized(false);
      dealerAdmin.enter(dealerId, requestedTenantId, {
        tenantName: assignedTenantDisplayName(tenantSelection.tenant)
          || assignedTenantDisplayName(overviewResponse.data),
        algoDeviceSupport: overviewResponse.data?.algoDeviceSupport === true,
      });
    }).catch((err) => {
      if (!live) return;
      setOverview(null);
      setSupport(null);
      if (String(err?.message || '').includes('(403)')) setUnauthorized(true);
      else setError(safeDealerError(err));
    });
    return () => {
      live = false;
    };
  }, [dealerAdmin.enter, requestedTenantId, selection.dealer, tenantSelection.unauthorized, tenantsLoaded]);

  if (!authReady || !agreementStatusReady) {
    return <MainLayout scrollMain><Box sx={{ p: 3 }}><Typography>Loading dealer workspace…</Typography></Box></MainLayout>;
  }
  if (!currentUser) {
    rememberPathForDealerLogin({
      hostname: typeof window !== 'undefined' ? window.location.hostname : '',
      pathname: location.pathname,
    });
    return <Navigate to={ROUTES.LOGIN} replace />;
  }
  if (shouldRedirectToOnboard({ pathname: location.pathname, agreementStatus })) {
    return <Navigate to={ROUTES.ONBOARD} replace />;
  }
  if (!contexts) {
    return <MainLayout scrollMain><Box sx={{ p: 3 }}><Typography>Loading dealer workspace…</Typography></Box></MainLayout>;
  }
  if (!contexts.length) {
    return (
      <MainLayout scrollMain>
        <Box sx={{ p: 3 }}>
          <Alert severity="info">No Dealer Access</Alert>
        </Box>
      </MainLayout>
    );
  }
  if (unauthorized || selection.unauthorized) {
    return (
      <NotAuthorized
        onLeave={() => {
          const dealerId = selection.dealer?.dealerId || requestedDealerId || dealerAdmin.dealerId;
          dealerAdmin.leave();
          navigate(dealerId
            ? ROUTES.DEALER_WORKSPACE_DEALER.replace(':dealerId', dealerId)
            : ROUTES.DEALER_WORKSPACE);
        }}
      />
    );
  }

  const chooseDealer = (nextId) => {
    navigate(ROUTES.DEALER_WORKSPACE_DEALER.replace(':dealerId', nextId));
  };
  const chooseTenant = (nextId) => {
    if (!selection.dealer) return;
    navigate(
      ROUTES.DEALER_WORKSPACE_TENANT
        .replace(':dealerId', selection.dealer.dealerId)
        .replace(':tenantId', nextId)
    );
  };

  return (
    <MainLayout scrollMain>
      <Box sx={{ p: 3, maxWidth: 880 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>Dealer workspace</Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Tenants assigned to your Dealer. Manage Tenant opens the existing tenant administration tools in a validated Dealer context.
        </Typography>
        {userRole === ROLES.TENANT_ADMIN || userRole === ROLES.ADMIN || userRole === ROLES.SUPERADMIN ? (
          <Button sx={{ mb: 2 }} onClick={() => { dealerAdmin.leave(); navigate(ROUTES.HOME_LAUNCHER); }}>
            Return to tenant
          </Button>
        ) : dealerAdmin.active ? (
          <Button sx={{ mb: 2 }} onClick={() => {
            const dealerId = selection.dealer?.dealerId || dealerAdmin.dealerId;
            dealerAdmin.leave();
            navigate(ROUTES.DEALER_WORKSPACE_DEALER.replace(':dealerId', dealerId));
          }}>
            Leave tenant
          </Button>
        ) : null}
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        {selection.needsSelection ? (
          <TextField select fullWidth label="Dealer" value="" onChange={(event) => chooseDealer(event.target.value)} sx={{ mb: 2 }}>
            {contexts.map((dealer) => (
              <MenuItem key={dealer.dealerId} value={dealer.dealerId}>{dealer.name}</MenuItem>
            ))}
          </TextField>
        ) : null}
        {contexts.length > 1 && selection.dealer ? (
          <TextField select fullWidth label="Dealer" value={selection.dealer.dealerId} onChange={(event) => chooseDealer(event.target.value)} sx={{ mb: 2 }}>
            {contexts.map((dealer) => (
              <MenuItem key={dealer.dealerId} value={dealer.dealerId}>{dealer.name}</MenuItem>
            ))}
          </TextField>
        ) : null}
        {selection.dealer ? (
          <TextField
            select
            fullWidth
            label="Assigned tenant"
            value={requestedTenantId || tenantSelection.tenant?.tenantId || ''}
            onChange={(event) => chooseTenant(event.target.value)}
            sx={{ mb: 3 }}
          >
            {tenants.map((tenant) => (
              <MenuItem key={tenant.tenantId} value={tenant.tenantId}>{tenant.name || tenant.tenantId}</MenuItem>
            ))}
          </TextField>
        ) : null}
        {overview ? (
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Typography variant="h5">Overview</Typography>
            <Typography>{overview.name} · {overview.status}</Typography>
            <Typography>Users: {overview.userCount}</Typography>
            <Typography>Devices: {overview.devices?.online ?? 0} online of {overview.devices?.total ?? 0}</Typography>
            <Typography>Agents: {overview.agents?.online ?? 0} online of {overview.agents?.total ?? 0}</Typography>
            <Typography>
              Features: {Object.entries(overview.features || {}).map(([key, value]) => `${key} ${value ? 'on' : 'off'}`).join(', ') || 'None'}
            </Typography>
            <Typography>Sites: {(overview.sites || []).map((site) => site.label || site.name).filter(Boolean).join(', ') || 'None'}</Typography>
            <Typography variant="h5" sx={{ mt: 2 }}>Support</Typography>
            <Typography>Open reports are counted without message content.</Typography>
            <Typography>Total: {support?.total ?? overview.support?.total ?? 0}</Typography>
            {Object.entries(support?.byStatus || overview.support?.byStatus || {}).map(([status, count]) => (
              <Typography key={status}>{status}: {count}</Typography>
            ))}
            <Button
              variant="contained"
              sx={{ mt: 2, justifySelf: 'start' }}
              onClick={() => {
                const tenantId = requestedTenantId || overview.tenantId || tenantSelection.tenant?.tenantId;
                dealerAdmin.enter(selection.dealer.dealerId, tenantId, {
                  tenantName: assignedTenantDisplayName(tenantSelection.tenant) || assignedTenantDisplayName(overview),
                });
                navigate(ROUTES.USER_MANAGEMENT);
              }}
            >
              Manage Tenant
            </Button>
            <CustomerSubscriptionSection dealerId={selection.dealer.dealerId} tenantId={requestedTenantId || overview.tenantId} />
            <DealerUsageReport dealerId={selection.dealer.dealerId} tenantId={requestedTenantId || overview.tenantId} />
          </Box>
        ) : null}
      </Box>
    </MainLayout>
  );
}
